import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import { trackOfferClick } from '../../services/offerClickService';
import { REGION_HREFLANG } from '../../config/publicRegions';
import { regionFromPathname } from '../../utils/regionFromPath';

const Section = styled.aside`
  margin-top: 0;
  min-width: 0;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.card};
  border-radius: ${({ theme }) => theme.radiusSm};
  box-shadow: ${({ theme }) => theme.shadowCard};
  padding: 1.5rem;
  min-width: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const HeaderCopy = styled.div`
  min-width: 0;
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontClamp?.xl || '1.25rem'};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0 0 0.35rem;
`;

const SponsoredNote = styled.p`
  margin: 0;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const HeaderNav = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-shrink: 0;
`;

const NavBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radiusSm || '8px'};
  background: ${({ theme }) => theme.colors.card};
  color: ${({ theme }) => theme.colors.foreground};
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s, border-color 0.15s;
  opacity: ${({ $visible }) => ($visible ? 1 : 0.35)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.muted};
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    cursor: default;
  }
`;

const CarouselWrap = styled.div`
  position: relative;
  min-width: 0;
`;

const CarouselTrack = styled.div`
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  scroll-behavior: smooth;
  scroll-snap-type: x proximity;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding: 0.15rem 0;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const OfferCard = styled.article`
  flex: 0 0 auto;
  width: min(100%, 23rem);
  min-width: 19.5rem;
  scroll-snap-align: start;
  display: grid;
  grid-template-columns: 5rem minmax(0, 1fr) auto;
  gap: 0.875rem;
  align-items: center;
  padding: 0.875rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radiusSm || '12px'};
  background: ${({ theme }) => theme.colors.card};
  box-shadow: ${({ theme }) => theme.shadowCard || 'none'};
`;

const OfferImage = styled.img`
  width: 5rem;
  height: 5rem;
  border-radius: ${({ theme }) => theme.radiusSm || '10px'};
  object-fit: cover;
  object-position: center;
  background: ${({ theme }) => theme.colors.muted};
  border: 1px solid ${({ theme }) => theme.colors.border};
  flex-shrink: 0;
`;

const ImagePlaceholder = styled.div`
  width: 5rem;
  height: 5rem;
  border-radius: ${({ theme }) => theme.radiusSm || '10px'};
  background: ${({ theme }) => theme.colors.muted};
  border: 1px dashed ${({ theme }) => theme.colors.border};
  flex-shrink: 0;
`;

const OfferBody = styled.div`
  min-width: 0;
`;

const OFFER_LINK_REL = 'nofollow sponsored noopener noreferrer';

const OfferTitleLink = styled.a`
  margin: 0 0 0.25rem;
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.foreground};
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: underline;
  }
`;

const OfferDescription = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.mutedForeground};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const OfferButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: ${({ theme }) => theme.radiusSm || '10px'};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground || '#fff'};
  text-decoration: none;
  flex-shrink: 0;
  align-self: center;
`;

export default function MediaOffers({
  offers = [],
  category = '',
  tmdbId = null,
  titleKey = 'media.detail.offersTitle',
  titleFallback = 'Partner offers',
}) {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const region = regionFromPathname(pathname);
  const language = REGION_HREFLANG[region] || 'en-US';
  const list = (Array.isArray(offers) ? offers : []).filter((o) => o && (o.title || o.url));
  const trackRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 4);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = trackRef.current;
    if (!el) return undefined;

    el.addEventListener('scroll', updateScrollState, { passive: true });
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
    };
  }, [list.length, updateScrollState]);

  if (!list.length) return null;

  const onOfferClick = (offer, index) => {
    trackOfferClick({
      category,
      tmdbId,
      language,
      offerIndex: index,
      offerTitle: offer.title,
      offerUrl: offer.url,
    });
  };

  const scrollBy = (direction) => {
    const el = trackRef.current;
    if (!el) return;
    const amount = Math.max(el.clientWidth * 0.8, 320);
    el.scrollBy({ left: direction * amount, behavior: 'smooth' });
  };

  const showNav = canScrollLeft || canScrollRight;

  return (
    <Section aria-label={t(titleKey, titleFallback)}>
      <Card>
        <HeaderRow>
          <HeaderCopy>
            <SectionTitle>{t(titleKey, titleFallback)}</SectionTitle>
            <SponsoredNote>{t('media.detail.sponsored', 'Sponsored')}</SponsoredNote>
          </HeaderCopy>
          {showNav ? (
            <HeaderNav aria-hidden={!showNav}>
              <NavBtn
                type="button"
                $visible={canScrollLeft}
                disabled={!canScrollLeft}
                onClick={() => scrollBy(-1)}
                aria-label={t('common.scrollPrevious', 'Scroll previous offers')}
              >
                <ChevronLeft size={18} />
              </NavBtn>
              <NavBtn
                type="button"
                $visible={canScrollRight}
                disabled={!canScrollRight}
                onClick={() => scrollBy(1)}
                aria-label={t('common.scrollNext', 'Scroll next offers')}
              >
                <ChevronRight size={18} />
              </NavBtn>
            </HeaderNav>
          ) : null}
        </HeaderRow>

        <CarouselWrap>
          <CarouselTrack ref={trackRef}>
            {list.map((offer, index) => {
              const href = String(offer.url || '').trim();
              const label = offer.buttonLabel || t('media.detail.viewOffer', 'View offer');
              return (
                <OfferCard key={`${href}-${index}`}>
                  {offer.imageUrl ? (
                    <OfferImage src={offer.imageUrl} alt={offer.title || ''} loading="lazy" />
                  ) : (
                    <ImagePlaceholder aria-hidden="true" />
                  )}
                  <OfferBody>
                    {href ? (
                      <OfferTitleLink
                        href={href}
                        target="_blank"
                        rel={OFFER_LINK_REL}
                        onClick={() => onOfferClick(offer, index)}
                      >
                        {offer.title || label}
                      </OfferTitleLink>
                    ) : (
                      <h3
                        style={{
                          margin: '0 0 0.25rem',
                          fontSize: '0.9375rem',
                          fontWeight: 600,
                          lineHeight: 1.35,
                        }}
                      >
                        {offer.title || label}
                      </h3>
                    )}
                    {offer.description ? <OfferDescription>{offer.description}</OfferDescription> : null}
                  </OfferBody>
                  {href ? (
                    <OfferButton
                      href={href}
                      target="_blank"
                      rel={OFFER_LINK_REL}
                      aria-label={label}
                      title={label}
                      onClick={() => onOfferClick(offer, index)}
                    >
                      <ExternalLink size={17} />
                    </OfferButton>
                  ) : null}
                </OfferCard>
              );
            })}
          </CarouselTrack>
        </CarouselWrap>
      </Card>
    </Section>
  );
}
